/**
 * Tenant subdomains: each organization's portal lives at {slug}.{base domain}
 * (acme.kaamco.com). VITE_TENANT_BASE_DOMAIN turns them on; unset, the app
 * behaves as a single host and every helper here is a no-op.
 * Dev: VITE_TENANT_BASE_DOMAIN=localhost, then open http://acme.localhost:2026.
 */
const BASE_DOMAIN =
  (import.meta.env.VITE_TENANT_BASE_DOMAIN as string | undefined)?.trim().toLowerCase() || null

/** Hosts under the base domain that are ours, never a tenant (mirrors the API's reserved slugs). */
const NON_TENANT_LABELS = new Set(['www', 'api', 'app', 'admin', 'console', 'platform', 'status'])

/** Hosts that serve this app but are ours or our providers', never a customer's domain. */
const PLATFORM_HOST_SUFFIXES = ['amplifyapp.com', 'cloudfront.net', 'ngrok.app', 'localhost']

export function isTenantSubdomainsEnabled(): boolean {
  return BASE_DOMAIN !== null
}

/**
 * A customer's own domain (hr.acme.com) pointed at KAAMCO. Known from the
 * hostname alone, before any request: the API is then reached through this
 * same host (/api, proxied by Caddy), so its cookies stay first-party.
 */
export function isCustomDomainHost(hostname: string = window.location.hostname): boolean {
  if (!BASE_DOMAIN) return false
  const host = hostname.toLowerCase()
  if (host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`)) return false
  if (/^[\d.]+$/.test(host) || host.includes(':')) return false
  return !PLATFORM_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))
}

/** Set once at startup on a custom domain, from the API's host lookup. */
let customDomainSlug: string | null = null

export function setCustomDomainTenant(slug: string | null): void {
  customDomainSlug = slug
}

/** API root (…/api): same-origin on a custom domain, else VITE_API_URL. */
export function apiRoot(): string {
  if (isCustomDomainHost()) return `${window.location.origin}/api`
  return (import.meta.env.VITE_API_URL ?? '').replace(/\/?$/, '')
}

/** Base domain tenants live under (e.g. "kaamco.com"), or null when subdomains are off. */
export function tenantBaseDomain(): string | null {
  return BASE_DOMAIN
}

/** The tenant slug this page is served under, or null on the main site. */
export function getTenantSlugFromHost(hostname: string = window.location.hostname): string | null {
  if (!BASE_DOMAIN) return null
  if (isCustomDomainHost(hostname)) return customDomainSlug
  const host = hostname.toLowerCase()
  const suffix = `.${BASE_DOMAIN}`
  if (!host.endsWith(suffix)) return null
  const label = host.slice(0, -suffix.length)
  if (!label || label.includes('.') || NON_TENANT_LABELS.has(label)) return null
  return label
}

function originFor(host: string): string {
  const { protocol, port } = window.location
  return `${protocol}//${host}${port ? `:${port}` : ''}`
}

/** Absolute URL of `path` in a tenant's portal. */
export function tenantUrl(slug: string, path = '/'): string {
  return `${originFor(`${slug}.${BASE_DOMAIN}`)}${path}`
}

/** Absolute URL of `path` on the main site (marketing, signup, generic login). */
export function apexUrl(path = '/'): string {
  if (!BASE_DOMAIN) return path
  const host = BASE_DOMAIN === 'localhost' ? 'localhost' : `www.${BASE_DOMAIN}`
  return `${originFor(host)}${path}`
}

/** Display form of a tenant's address, e.g. "acme.kaamco.com". */
export function tenantHostLabel(slug: string): string | null {
  return BASE_DOMAIN ? `${slug}.${BASE_DOMAIN}` : null
}

/** The address this page is on, for tenant-facing copy ("hr.acme.com" or "acme.kaamco.com"). */
export function currentTenantHostLabel(slug: string): string | null {
  return isCustomDomainHost() ? window.location.hostname : tenantHostLabel(slug)
}

/**
 * Where an organization's user belongs, when that is not this host: a full
 * URL to the same path on their own subdomain, or null when they are already
 * there (or subdomains are off).
 */
export function wrongTenantRedirect(organizationSlug: string | null | undefined): string | null {
  if (!BASE_DOMAIN || !organizationSlug) return null
  if (getTenantSlugFromHost() === organizationSlug) return null
  const { pathname, search, hash } = window.location
  return tenantUrl(organizationSlug, `${pathname}${search}${hash}`)
}
